import type { CalendarBackgroundTime } from '@Pages/pageRuntime/components/calendar/calendarTypes';
import type { SdkAvailabilityPiece } from '@Pages/pageRuntime/sdk/types';
import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone';
import { fromZonedTime } from 'date-fns-tz/fromZonedTime';

/** One record of the availability read, as the server sends it. */
export type AvailabilityServerRecord = {
  id: string;
  timezone: string;
  available: { start: string; end: string }[];
  pieces: SdkAvailabilityPiece[];
};

export type AvailabilityLabels = { busy: string; closed: string } | false;

type Interval = [number, number];

const DAY_MS = 86_400_000;

/**
 * Free time with the open tone, then the closed time inside the record's hours: busy time on
 * connected calendars as `labels.busy`, and exceptions, closures, and limits as `labels.closed`.
 * Closed time wins where both overlap.
 */
export function availabilityBackgroundTimes(
  record: AvailabilityServerRecord,
  labels: AvailabilityLabels
): CalendarBackgroundTime[] {
  const hours: Interval[] = [];
  const added: Interval[] = [];
  const replacedDays: Interval[] = [];
  const closed: Interval[] = [];
  const busy: Interval[] = [];

  for (const piece of record.pieces) {
    const interval = toInterval(piece);

    switch (piece.source) {
      case 'hours':
        hours.push(interval);
        break;
      case 'exception':
        if (piece.effect === 'closed') {
          closed.push(interval);
        } else {
          added.push(interval);
          if (piece.effect === 'replace') {
            replacedDays.push(...localDays(interval, record.timezone));
          }
        }
        break;
      case 'limit':
      case 'closure':
        closed.push(interval);
        break;
      case 'external':
        busy.push(interval);
        break;
      default: {
        const unknownSource: never = piece.source;
        throw new Error(`Unknown availability source: ${String(unknownSource)}`);
      }
    }
  }

  // Closure and closed exception pieces cover their whole interval, so the frame keeps them
  // inside the time that hours and open exceptions would make free.
  const frame = normalize([...subtract(normalize(hours), normalize(replacedDays)), ...added]);
  const available = normalize(record.available.map(toInterval));
  const closedParts = subtract(intersect(normalize(closed), frame), available);
  const busyParts = subtract(subtract(intersect(normalize(busy), frame), available), closedParts);

  const times: CalendarBackgroundTime[] = [
    ...record.available.map(({ start, end }) => ({ start, end, tone: 'open' as const })),
    ...closedParts.map((interval) => closedTime(interval, labels === false ? null : labels.closed)),
    ...busyParts.map((interval) => closedTime(interval, labels === false ? null : labels.busy))
  ];

  return times.toSorted((a, b) => Date.parse(String(a.start)) - Date.parse(String(b.start)));
}

function toInterval({ start, end }: { start: string; end: string }): Interval {
  return [Date.parse(start), Date.parse(end)];
}

function closedTime([start, end]: Interval, label: string | null): CalendarBackgroundTime {
  const time: CalendarBackgroundTime = {
    start: new Date(start).toISOString(),
    end: new Date(end).toISOString(),
    tone: 'closed'
  };

  if (label !== null) {
    time.label = label;
  }

  return time;
}

/** Each local date that the interval touches, from local midnight to the next. The end is exclusive. */
function localDays([start, end]: Interval, timezone: string): Interval[] {
  const days: Interval[] = [];
  let date = formatInTimeZone(start, timezone, 'yyyy-MM-dd');
  let dayStart = fromZonedTime(`${date}T00:00`, timezone).getTime();

  while (dayStart < end) {
    date = new Date(Date.parse(`${date}T00:00:00Z`) + DAY_MS).toISOString().slice(0, 10);
    const dayEnd = fromZonedTime(`${date}T00:00`, timezone).getTime();
    days.push([dayStart, dayEnd]);
    dayStart = dayEnd;
  }

  return days;
}

function normalize(intervals: Interval[]): Interval[] {
  const sorted = intervals.filter(([start, end]) => start < end).toSorted((a, b) => a[0] - b[0]);
  const merged: Interval[] = [];

  for (const [start, end] of sorted) {
    const last = merged.at(-1);

    if (last !== undefined && start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  }

  return merged;
}

/** Both inputs are normalized. */
function intersect(a: Interval[], b: Interval[]): Interval[] {
  const result: Interval[] = [];
  let i = 0;
  let j = 0;

  while (i < a.length && j < b.length) {
    const start = Math.max(a[i][0], b[j][0]);
    const end = Math.min(a[i][1], b[j][1]);

    if (start < end) {
      result.push([start, end]);
    }

    if (a[i][1] < b[j][1]) {
      i++;
    } else {
      j++;
    }
  }

  return result;
}

/** Both inputs are normalized. */
function subtract(a: Interval[], b: Interval[]): Interval[] {
  const result: Interval[] = [];
  let j = 0;

  for (const [start, end] of a) {
    let cursor = start;

    while (j < b.length && b[j][1] <= cursor) {
      j++;
    }

    for (let k = j; k < b.length && b[k][0] < end; k++) {
      if (b[k][0] > cursor) {
        result.push([cursor, b[k][0]]);
      }
      cursor = Math.max(cursor, b[k][1]);
    }

    if (cursor < end) {
      result.push([cursor, end]);
    }
  }

  return result;
}
